import App from "@/app";
import ProtectedRouteProvider from "@/core/providers/protected-route.provider";
import { BaseLayout } from "@components/layouts/base.layout";
import { USER_TYPE } from "@features/authentication";
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import RoleGuard from "../providers/role-guard.provider";
import EDITOR_ROUTES from "./editor-routes";
import PROFILE_ROUTES from "./profile-routes";

const Login = lazy(() => import("@/pages/login"));
const SignUp = lazy(() => import("@/pages/sign-up"));
// The funnel runs on its own in-memory router; the protected /pages/wizard
// route hosts it, always authenticated.
const HowToStart = lazy(() => import("@/pages/how-to-start"));
const NotFound = lazy(() => import("@/pages/not-found"));
const Home = lazy(() => import("@pages/home"));
const IntegrationShare = lazy(() => import("@pages/integration-share"));
const Analytics = lazy(() => import("@pages/analytics"));
const Automation = lazy(() => import("@pages/automation"));
const Hubspot = lazy(() => import("@pages/hubspot"));
const ArchiveVersion = lazy(
  () => import("@pages/integration-share/components/archive"),
);

const Salesforce = lazy(() => import("@pages/salesforce"));
const ChromeExtension = lazy(() => import("@pages/chrome-extension"));
const McpConnect = lazy(() => import("@pages/mcp-connect"));
const Account = lazy(() => import("@pages/account"));
const Settings = lazy(() => import("@pages/settings"));
const Design = lazy(() => import("@pages/design"));
const OnboardingHub = lazy(
  () => import("@features/platform-onboarding/components/onboarding-hub"),
);
const Variables = lazy(() => import("@/pages/variables"));

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    children: [
      {
        path: "/",
        element: <Navigate to="/pages/home" replace />,
      },
      {
        path: "/login",
        element: (
          <Suspense fallback>
            <Login />
          </Suspense>
        ),
      },
      {
        path: "/sign-up",
        element: (
          <Suspense fallback>
            <SignUp />
          </Suspense>
        ),
      },
      {
        path: "pages",
        element: <ProtectedRouteProvider />,
        children: [
          {
            index: true,
            element: <Navigate to="/pages/home" replace />,
          },
          {
            path: "home",
            element: (
              <BaseLayout title="Dashboard" fillViewport>
                <Home />
              </BaseLayout>
            ),
          },
          {
            path: "analytics",
            element: (
              <BaseLayout title="Analytics">
                <Analytics />
              </BaseLayout>
            ),
          },
          {
            path: "wizard/*",
            element: (
              <RoleGuard notAllowed={[USER_TYPE.USER, USER_TYPE.CREATOR_LITE]}>
                <Suspense fallback>
                  <HowToStart />
                </Suspense>
              </RoleGuard>
            ),
          },
          {
            path: "integrations/salesforce",
            element: (
              <BaseLayout hideHeader hideSidebar>
                <Salesforce />
              </BaseLayout>
            ),
          },
          {
            path: "integrations/hubspot-iframe",
            element: (
              <BaseLayout hideHeader hideSidebar>
                <IntegrationShare />
              </BaseLayout>
            ),
          },
          {
            path: "integrations/hubspot",
            element: (
              <BaseLayout hideHeader hideSidebar>
                <Hubspot />
              </BaseLayout>
            ),
          },
          {
            // The analytics dashboard without the app chrome, for HubSpot's
            // timeline `timelineIFrame` modal. App-event templates render as
            // markdown with HTML stripped, so a link in the card body can only
            // navigate the user's HubSpot tab away — the modal is the platform's
            // only way to keep them on the record they were looking at.
            path: "integrations/hubspot-analytics",
            element: (
              <BaseLayout hideHeader hideSidebar>
                <Analytics />
              </BaseLayout>
            ),
          },
          {
            path: "integrations/mcp",
            element: (
              <BaseLayout hideHeader hideSidebar noPadding>
                <McpConnect />
              </BaseLayout>
            ),
          },
          {
            path: "integrations/chrome-extension",
            element: (
              <BaseLayout hideHeader hideSidebar noPadding>
                <ChromeExtension />
              </BaseLayout>
            ),
          },
          {
            path: "integrations/hubspot-iframe/delete/:versionId",
            element: (
              <BaseLayout hideHeader hideSidebar>
                <ArchiveVersion />
              </BaseLayout>
            ),
          },
          {
            path: "automations",
            element: (
              <BaseLayout title="Automations">
                <Automation />
              </BaseLayout>
            ),
          },
          {
            path: "account",
            element: (
              <RoleGuard notAllowed={[USER_TYPE.USER, USER_TYPE.CREATOR_LITE]}>
                <BaseLayout title="Organization settings">
                  <Account />
                </BaseLayout>
              </RoleGuard>
            ),
          },
          {
            path: "settings",
            element: (
              <BaseLayout title="User settings">
                <Settings />
              </BaseLayout>
            ),
          },
          {
            path: "onboarding",
            element: (
              <BaseLayout title="Onboarding Hub">
                <OnboardingHub />
              </BaseLayout>
            ),
          },
          {
            path: "design",
            element: (
              <RoleGuard notAllowed={[USER_TYPE.USER, USER_TYPE.CREATOR_LITE]}>
                <BaseLayout title="Design">
                  <Design />
                </BaseLayout>
              </RoleGuard>
            ),
          },
          {
            path: "variables",
            element: (
              <RoleGuard notAllowed={[USER_TYPE.USER, USER_TYPE.CREATOR_LITE]}>
                <BaseLayout title="Variables">
                  <Variables />
                </BaseLayout>
              </RoleGuard>
            ),
          },
          PROFILE_ROUTES,
          EDITOR_ROUTES,
        ],
      },
      {
        path: "*",
        element: (
          <ProtectedRouteProvider>
            <Suspense fallback>
              <NotFound />
            </Suspense>
          </ProtectedRouteProvider>
        ),
      },
    ],
  },
]);
